import { uploadResponse } from '@xdocs/schemas';
import type { UploadResponse } from '@xdocs/schemas';

/**
 * Client-side transport for the ONE REST escape hatch this feature owns
 * (`POST /upload`, F18/§5.8 — the only non-WS ingest entry). Uses
 * `XMLHttpRequest` rather than `fetch` specifically for `upload.onprogress`
 * (per-byte upload progress) — `fetch`'s request-body streaming isn't
 * uniformly readable for progress across browsers yet.
 *
 * Assumed wire contract (server not yet built — flagged cross-team wiring
 * dependency, `apps/server/**` out of this task's ownership, same pattern
 * as `drive.ts`'s own doc comment for its OAuth callback dependency):
 * multipart/form-data with one `files` part per `File` (this transport
 * always sends exactly ONE file per request — see `useUploadQueue.ts` for
 * why), one `meta` part carrying `JSON.stringify(UploadRequestMeta)`, auth
 * via `Authorization: Bearer <accessToken>` (no session cookie carries the
 * in-memory access token — same header the WS handshake payload carries),
 * response body JSON-parses to `UploadResponse`.
 */

const DEV_UPLOAD_PORT = 8080;

function deriveDevUploadUrl(): string {
  if (typeof window === 'undefined') return `http://localhost:${DEV_UPLOAD_PORT}/upload`;
  const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
  // Vite dev server proxies /upload to @xdocs/server, mirroring the /ws
  // proxy entry in apps/web/vite.config.ts.
  return `${protocol}//${window.location.host}/upload`;
}

export function resolveUploadUrl(): string {
  const configured = import.meta.env.VITE_UPLOAD_URL;
  if (configured && configured.trim().length > 0) return configured;
  return deriveDevUploadUrl();
}

export class UploadTransportError extends Error {
  readonly reason: 'network' | 'aborted' | 'server';
  readonly status: number | undefined;

  constructor(reason: 'network' | 'aborted' | 'server', message: string, status?: number) {
    super(message);
    this.name = 'UploadTransportError';
    this.reason = reason;
    this.status = status;
  }
}

export interface UploadProgressEvent {
  loaded: number;
  total: number;
}

export interface UploadOneFileOptions {
  url: string;
  accessToken: string;
  file: File;
  folderId: string | null;
  onProgress?: (event: UploadProgressEvent) => void;
  signal?: AbortSignal;
}

/** Uploads exactly one `File` via a single multipart request, reporting
 * granular upload progress for that one file (no aggregate-progress
 * approximation needed, unlike a true multi-file-per-request batch). */
export function uploadOneFile({ url, accessToken, file, folderId, onProgress, signal }: UploadOneFileOptions): Promise<UploadResponse> {
  return new Promise<UploadResponse>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
    xhr.responseType = 'text';

    function cleanup(): void {
      if (signal) signal.removeEventListener('abort', onAbort);
    }

    function onAbort(): void {
      xhr.abort();
    }

    if (signal) {
      if (signal.aborted) {
        reject(new UploadTransportError('aborted', 'errors:upload.aborted'));
        return;
      }
      signal.addEventListener('abort', onAbort);
    }

    xhr.upload.onprogress = (event) => {
      if (!onProgress) return;
      onProgress({ loaded: event.loaded, total: event.lengthComputable ? event.total : file.size });
    };

    xhr.onload = () => {
      cleanup();
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const json: unknown = JSON.parse(xhr.responseText);
          resolve(uploadResponse.parse(json));
        } catch {
          reject(new UploadTransportError('server', 'errors:code.INTERNAL', xhr.status));
        }
        return;
      }
      reject(new UploadTransportError('server', 'errors:code.INTERNAL', xhr.status));
    };

    xhr.onerror = () => {
      cleanup();
      reject(new UploadTransportError('network', 'errors:upload.network'));
    };

    xhr.onabort = () => {
      cleanup();
      reject(new UploadTransportError('aborted', 'errors:upload.aborted'));
    };

    const formData = new FormData();
    formData.append('meta', JSON.stringify({ folderId }));
    formData.append('files', file, file.name);
    xhr.send(formData);
  });
}
